import {
    ComponentParams,
    ComponentRendering,
    Text as JssText,
} from "@sitecore-jss/sitecore-jss-nextjs";
import {
    Box,
    Button,
    Link,
    Heading,
    Stack,
    Text,
    keyframes,
    AspectRatio,
    Flex,
} from "@chakra-ui/react";
import GenericHeader, { GenericHeaderProps } from "components/ui/GenericHeader";
import { useEffect, useRef, useState } from "react";
import NextImage from "next/image";
import useImageSizes from "hooks/useImageSizes";
import NextLink from "next/link";
import { useI18n } from "next-localization";
import { pxToRem } from "themes/utils/mapPxToRem";
import { dateFormatter } from "utils/dateFormatter";

interface Fields extends GenericHeaderProps {
    TaglinesFilter: {
        id: string;
    }[];
    TaglinesFilterStartItem: {
        id: string;
    };
}

type PageFieldValue = {
    jsonValue: {
        alt: string;
        src: string;
        displayName: string;
        value: string;
    };
};

interface Result {
    id: string;
    pageListTagline: PageFieldValue;
    pageListHeadline: PageFieldValue;
    pageListTeaserText: PageFieldValue;
    pageListImage: {
        jsonValue: {
            value: {
                alt: string;
                src: string;
                width: number;
                height: number;
            };
        };
    };
    date: PageFieldValue;
    endDate: PageFieldValue;
    url: {
        url: string;
        path: string;
    };
}

type SkeletonProps = {
    width?: string;
    height?: string;
    aspectRatio?: string;
};

export type DynamicLinkListProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
};

const Skeleton = (props: SkeletonProps) => {
    return (
        <Box
            width={props.width}
            height={props.height}
            bg="brand.lightGray"
            borderRadius="base"
            aspectRatio={props.aspectRatio}
        />
    );
};

const SkeletonContainer = () => {
    const pulseLoadingAnimation = keyframes`
   0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
`;
    const loadingAnimation = `${pulseLoadingAnimation} infinite 2s linear`;
    return (
        <Stack spacing="sp16" w="full" animation={loadingAnimation}>
            <Skeleton aspectRatio="4/3" />
            <Box display={"grid"} gridTemplateColumns={"1fr 1fr"} gap="sp16">
                <Skeleton height={pxToRem("12px")} />
                <Skeleton height={pxToRem("12px")} />
            </Box>
            <Stack spacing="sp4">
                <Skeleton width="80%" height={pxToRem("16px")} />
                <Skeleton width="70%" height={pxToRem("16px")} />
                <Skeleton width="90%" height={pxToRem("16px")} />
            </Stack>
            <Stack spacing="sp4">
                <Skeleton width="70%" height={pxToRem("12px")} />
                <Skeleton width="60%" height={pxToRem("12px")} />
                <Skeleton width="80%" height={pxToRem("12px")} />
            </Stack>
        </Stack>
    );
};

const DynamicLinkList = (props: DynamicLinkListProps) => {
    const sizes = useImageSizes({ base: 1, sm: 1 / 2, lg: 1 / 3 });
    const [dynamicListResults, setDynamicListResults] = useState<{
        results: Result[];
        firstNewResultIndex: number;
    }>({
        results: [],
        firstNewResultIndex: -1,
    });
    const [endCursor, setEndCursor] = useState<string>("");
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const numberOfResultsPerPage = 12;
    const { t } = useI18n();
    const firstNewResultRef = useRef<any>(null);

    const fetchResults = async () => {
        setLoading(true);
        try {
            const requestBody = {
                tagLineStartId: props.fields.TaglinesFilterStartItem?.id,
                tagLineIds: props.fields.TaglinesFilter.map((tagLine) => tagLine?.id),
                numberOfResultsPerPage: numberOfResultsPerPage,
                afterId: endCursor ?? "",
            };
            const response = await fetch(`/api/dynamic-link-list`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                throw new Error("Failed to fetch data");
            }

            const responseData = await response.json();
            const filteredResults = responseData.results.filter((item: Result) => item !== null);

            setDynamicListResults({
                results: [...dynamicListResults.results, ...filteredResults],
                firstNewResultIndex: dynamicListResults.results.length,
            });
            setEndCursor(responseData.endCursor);
            setHasNext(responseData.hasNext);
        } catch (error) {
            console.log("error fetching data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResults();
    }, []);

    useEffect(() => {
        // if (firstNewResultRef?.current) {
        //     firstNewResultRef?.current.scrollIntoView({
        //         behavior: "smooth",
        //         block: "start",
        //     });
        //     firstNewResultRef?.current.focus();
        // }
    }, [dynamicListResults.firstNewResultIndex]);

    const renderLoadMoreButtonLabel = () => {
        if (loading) {
            return t("loadMoreLoadingText");
        } else if (hasNext) {
            return t("fetchMoreResultsText");
        } else {
            return t("noMoreResultsText");
        }
    };

    return (
        <GenericHeader
            Id={props.rendering?.uid}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
        >
            <Box
                display="grid"
                gridTemplateRows="repeat(5, auto)"
                gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                gap="sp40"
                aria-live={"polite"}
                aria-busy={loading}
                id={props.fields?.Id}
            >
                {loading
                    ? Array.from({ length: numberOfResultsPerPage }, (_, index) => (
                          <SkeletonContainer key={index} />
                      ))
                    : dynamicListResults?.results?.map((item, index) => {
                          const isFirstNewResult = index === dynamicListResults.firstNewResultIndex;
                          let date = item?.date && dateFormatter(item?.date?.jsonValue.value);
                          const toDate =
                              item?.endDate && dateFormatter(item?.endDate?.jsonValue.value);
                          if (date && toDate) {
                              date = `${date} - ${toDate}`;
                          }
                          return (
                              <Link
                                  as={NextLink}
                                  ref={isFirstNewResult ? firstNewResultRef : null}
                                  display="grid"
                                  gridTemplateRows="subgrid"
                                  gap="sp16"
                                  key={item?.id}
                                  href={item?.url?.path}
                                  id={item?.id}
                                  _hover={{
                                      span: {
                                          _before: {
                                              right: "100%",
                                          },
                                      },
                                  }}
                              >
                                  <Flex
                                      as="article"
                                      minWidth="100%"
                                      flexDirection="column"
                                      gap="sp16"
                                  >
                                      <AspectRatio
                                          borderRadius="base"
                                          overflow="hidden"
                                          ratio={4 / 3}
                                      >
                                          {item?.pageListImage?.jsonValue?.value?.src ? (
                                              <NextImage
                                                  src={item?.pageListImage?.jsonValue?.value?.src}
                                                  alt={
                                                      item?.pageListImage?.jsonValue?.value?.alt ??
                                                      ""
                                                  }
                                                  sizes={sizes}
                                                  width={Number(
                                                      item?.pageListImage?.jsonValue?.value?.width
                                                  )}
                                                  height={Number(
                                                      item?.pageListImage?.jsonValue?.value?.width *
                                                          (3 / 4)
                                                  )}
                                                  style={{
                                                      objectFit: "cover",
                                                      width: "100%",
                                                      height: "100%",
                                                  }}
                                              />
                                          ) : (
                                              <></>
                                          )}
                                      </AspectRatio>
                                      <Stack spacing="sp16">
                                          <Box
                                              display="grid"
                                              gridTemplateColumns={"1fr auto"}
                                              alignItems="center"
                                          >
                                              <Text as="p" size="eyebrowSmall">
                                                  <JssText
                                                      field={{
                                                          value: item?.pageListTagline?.jsonValue
                                                              ?.displayName,
                                                      }}
                                                      editable={false}
                                                  />
                                              </Text>
                                              {date && (
                                                  <Text as="p" size="articleDate" flexGrow="1">
                                                      {date}
                                                  </Text>
                                              )}
                                          </Box>
                                          <Heading as="h3" size="h5">
                                              <JssText
                                                  field={{
                                                      value: item?.pageListHeadline?.jsonValue
                                                          ?.value,
                                                  }}
                                                  editable={false}
                                              />
                                          </Heading>
                                          <Text as="p" size="bodySmall">
                                              <JssText
                                                  field={{
                                                      value: item?.pageListTeaserText?.jsonValue
                                                          ?.value,
                                                  }}
                                                  editable={false}
                                              />
                                          </Text>
                                          <Text as="span" my="sp12" variant="link" w="fit-content">
                                              {t("readTheArticle")}
                                          </Text>
                                      </Stack>
                                  </Flex>
                              </Link>
                          );
                      })}
            </Box>
            <Box display="flex" width="100%">
                <Button
                    mt="sp40"
                    mx="auto"
                    size="sm"
                    isLoading={loading}
                    onClick={(e) => {
                        e.preventDefault();
                        if (hasNext) {
                            fetchResults();
                        }
                    }}
                    aria-label={renderLoadMoreButtonLabel()}
                    aria-controls={props.fields?.Id}
                >
                    {t("loadMoreText")}
                </Button>
            </Box>
        </GenericHeader>
    );
};

export default DynamicLinkList;
